import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Copy } from "lucide-react";
import { api, gbp, formatApiErrorDetail } from "../lib/api";
import { useBasket } from "../context/BasketContext";

export default function CheckoutPage() {
  const { items, subtotal, clear } = useBasket();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", phone: "", address_line: "", city: "", postcode: "", delivery_method: "delivery" });
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payMethod, setPayMethod] = useState("bank");
  const [payConfig, setPayConfig] = useState({ paypal_enabled: false, open_banking_enabled: false });
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    api.get("/payments/config").then(({ data }) => setPayConfig(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.delivery_method !== "delivery" || form.postcode.trim().length < 3) { setQuote(null); return; }
    const t = setTimeout(() => {
      api.get("/address/lookup", { params: { postcode: form.postcode } })
        .then(({ data }) => {
          setQuote({ zone: data.zone, price: data.delivery_price, city: data.city, region: data.region, valid: true });
          setForm((f) => (f.city.trim() ? f : { ...f, city: data.city }));
        })
        .catch((err) => {
          if (err.response?.status === 422) {
            api.get("/delivery/quote", { params: { postcode: form.postcode } })
              .then(({ data }) => setQuote({ ...data, valid: false }))
              .catch(() => setQuote(null));
          } else {
            api.get("/delivery/quote", { params: { postcode: form.postcode } })
              .then(({ data }) => setQuote({ ...data, valid: null }))
              .catch(() => setQuote(null));
          }
        });
    }, 450);
    return () => clearTimeout(t);
  }, [form.postcode, form.delivery_method]);

  useEffect(() => {
    if (items.length === 0 && !order) navigate("/basket");
  }, [items.length, order, navigate]);

  const deliveryFee = form.delivery_method === "collection" ? 0 : quote ? quote.price : null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full bg-transparent border border-[#27272A] px-4 py-3.5 text-sm outline-none focus:border-[#FFCC00] placeholder:text-white/30";

  const placeOrder = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { ...form, items: items.map((i) => ({ deal_id: i.deal_id, qty: i.qty })) });
      clear();
      if (payMethod === "paypal") {
        try {
          const { data: pp } = await api.post("/payments/paypal/create", { order_id: data.id });
          window.location.assign(pp.approval_url);
          return;
        } catch (err) {
          toast.error(formatApiErrorDetail(err.response?.data?.detail) + " — showing bank transfer details instead.");
        }
      }
      setOrder(data);
      toast.success("Order placed. Stock reserved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  if (order) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-16">
        <div className="flex items-center gap-4 mb-8">
          <CheckCircle2 size={40} className="text-[#FFCC00]" />
          <h1 className="font-display text-4xl sm:text-5xl">ORDER <span className="text-[#FFCC00]">PLACED.</span></h1>
        </div>
        <div className="border-2 border-[#FFCC00] p-8">
          <div className="text-xs uppercase tracking-widest text-white/40 mb-2">Your payment reference — include it with your transfer</div>
          <div className="flex items-center gap-4">
            <span data-testid="order-payment-reference" className="font-display text-4xl text-[#FFCC00]">{order.reference}</span>
            <button data-testid="copy-reference-button" onClick={() => copy(order.reference)} className="btn-ghost-industrial p-2"><Copy size={16} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-px bg-[#27272A] border border-[#27272A] mt-8 text-sm">
            {[["Account name", order.bank_details.account_name], ["Bank", order.bank_details.bank], ["Sort code", order.bank_details.sort_code], ["Account number", order.bank_details.account_number], ["Amount due", gbp(order.total)], ["Status", "Awaiting payment"]].map(([k, v]) => (
              <div key={k} className="bg-[#0A0A0A] p-4">
                <div className="text-[10px] uppercase tracking-widest text-white/40">{k}</div>
                <div className="mt-1 text-white">{v}</div>
              </div>
            ))}
          </div>
          <p className="text-white/40 text-xs mt-6">Transfer within 24 hours to hold your stock. Reconciliation is automatic — you'll get a confirmation email once payment lands.</p>
        </div>
        <div className="flex gap-4 mt-8">
          <Link to="/account" data-testid="view-orders-link" className="btn-industrial px-8 py-3 text-lg">VIEW MY ORDERS</Link>
          <Link to="/deals" className="btn-ghost-industrial px-8 py-3 text-lg">BACK TO DEALS</Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-10">CHECK<span className="text-[#FFCC00]">OUT</span></h1>
      <form onSubmit={placeOrder} className="grid lg:grid-cols-[1fr_320px] gap-10">
        <div className="space-y-8">
          <div>
            <h3 className="font-display text-2xl mb-4"><span className="text-[#FF3B30]">01</span> CONTACT</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="checkout-name-input" required placeholder="FULL NAME" value={form.full_name} onChange={set("full_name")} className={inputCls} />
              <input data-testid="checkout-phone-input" required placeholder="PHONE" value={form.phone} onChange={set("phone")} className={inputCls} />
            </div>
          </div>
          <div>
            <h3 className="font-display text-2xl mb-4"><span className="text-[#FF3B30]">02</span> DELIVERY OR COLLECTION</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              {[["delivery", "PALLET DELIVERY — ZONE PRICED"], ["collection", "COLLECT FREE"]].map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`delivery-method-${v}`}
                  onClick={() => setForm({ ...form, delivery_method: v })}
                  className={`border px-4 py-4 font-display text-lg transition-colors duration-150 ${form.delivery_method === v ? "border-[#FFCC00] text-[#FFCC00]" : "border-[#27272A] text-white/50"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="space-y-4">
              <input data-testid="checkout-address-input" required placeholder="ADDRESS LINE" value={form.address_line} onChange={set("address_line")} className={inputCls} />
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="checkout-city-input" required placeholder="TOWN / CITY" value={form.city} onChange={set("city")} className={inputCls} />
                <input data-testid="checkout-postcode-input" required placeholder="POSTCODE" value={form.postcode} onChange={set("postcode")} className={inputCls} />
              </div>
              {quote && form.delivery_method === "delivery" && (
                <div data-testid="postcode-lookup-result" className={`text-xs px-4 py-3 border ${quote.valid ? "border-[#FFCC00] text-[#FFCC00]" : quote.valid === false ? "border-[#FF3B30] text-[#FF3B30]" : "border-[#27272A] text-white/50"}`}>
                  {quote.valid
                    ? `✓ ${quote.city}${quote.region ? `, ${quote.region}` : ""} — Zone ${quote.zone} delivery ${gbp(quote.price)}`
                    : quote.valid === false
                      ? `Postcode not recognised — quoting Zone ${quote.zone} (${gbp(quote.price)}). Double-check before ordering.`
                      : `Zone ${quote.zone} delivery ${gbp(quote.price)}`}
                </div>
              )}
            </div>
          </div>
          <div>
            <h3 className="font-display text-2xl mb-4"><span className="text-[#FF3B30]">03</span> PAYMENT METHOD</h3>
            <div className="space-y-3">
              <button
                type="button"
                data-testid="pay-method-bank"
                onClick={() => setPayMethod("bank")}
                className={`w-full text-left border p-5 transition-colors duration-150 ${payMethod === "bank" ? "border-[#FFCC00]" : "border-[#27272A]"}`}
              >
                <div className={`font-display text-xl ${payMethod === "bank" ? "text-[#FFCC00]" : "text-white/70"}`}>BANK TRANSFER</div>
                <div className="text-white/40 text-xs mt-1">Unique payment reference + our bank details. No fees, automatic reconciliation.</div>
              </button>
              <button
                type="button"
                data-testid="pay-method-paypal"
                onClick={() => payConfig.paypal_enabled && setPayMethod("paypal")}
                disabled={!payConfig.paypal_enabled}
                className={`w-full text-left border p-5 transition-colors duration-150 ${payMethod === "paypal" ? "border-[#FFCC00]" : "border-[#27272A]"} ${!payConfig.paypal_enabled ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <div className={`font-display text-xl ${payMethod === "paypal" ? "text-[#FFCC00]" : "text-white/70"}`}>PAYPAL</div>
                <div className="text-white/40 text-xs mt-1">
                  {payConfig.paypal_enabled ? "Pay instantly with your PayPal account. You'll be redirected to PayPal." : "Not yet activated — awaiting sandbox API keys."}
                </div>
              </button>
              <div data-testid="pay-method-openbanking" className="w-full text-left border border-[#27272A] p-5 opacity-50">
                <div className="font-display text-xl text-white/70 flex items-center gap-3">
                  PAY BY BANK — OPEN BANKING
                  <span className="bg-[#FF3B30] text-white text-[10px] tracking-widest px-2 py-0.5 font-sans">COMING SOON</span>
                </div>
                <div className="text-white/40 text-xs mt-1">One-tap bank payment via TrueLayer. Launching once Open Banking credentials are approved.</div>
              </div>
            </div>
          </div>
        </div>
        <div className="border border-[#27272A] p-8 h-fit">
          <h3 className="font-display text-2xl mb-6">ORDER</h3>
          {items.map((i) => (
            <div key={i.deal_id} className="flex justify-between text-sm mb-2">
              <span className="text-white/60 truncate mr-3">{i.qty}x {i.title}</span>
              <span className="shrink-0">{gbp(i.price * i.qty)}</span>
            </div>
          ))}
          <div className="border-t border-[#27272A] mt-4 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-white/50">Subtotal</span><span>{gbp(subtotal)}</span></div>
            <div className="flex justify-between">
              <span className="text-white/50">Delivery{quote && form.delivery_method === "delivery" ? ` (Zone ${quote.zone})` : ""}</span>
              <span data-testid="checkout-delivery-fee">
                {form.delivery_method === "collection" ? "FREE" : deliveryFee != null ? gbp(deliveryFee) : "Enter postcode"}
              </span>
            </div>
            <div className="flex justify-between font-display text-2xl pt-2"><span>TOTAL</span><span data-testid="checkout-total" className="text-[#FFCC00]">{gbp(subtotal + (deliveryFee || 0))}</span></div>
          </div>
          <button data-testid="place-order-button" disabled={busy} className="btn-industrial w-full py-4 text-xl mt-6">
            {busy ? "PLACING…" : payMethod === "paypal" ? "PLACE ORDER & PAY WITH PAYPAL" : "PLACE ORDER"}
          </button>
        </div>
      </form>
    </div>
  );
}
